/**
 * Department reconciliation card, shown after a preview finds departments that
 * need a decision before the run can post.
 *
 * Two kinds of row:
 *   - new       — a department with staff this month that isn't in the admin
 *                 table at all; Simon picks its iplicit department here.
 *   - mismapped — a department whose stored iplicit code is not an active iplicit
 *                 department; the existing values are pre-filled so he can correct.
 *
 * Departments with no staff this month never reach this card — they're listed as
 * "ignored" and left out of the journal entirely.
 *
 * "Apply & continue" writes these rows to the admin table (subject to the
 * overwrite pill) and proceeds with the import. "Cancel import" discards the file.
 */
import { useMemo, useState } from "react";
import type { DeptClass, DeptEdit, ImportPreview } from "../../payroll/api";

const CLASSES: DeptClass[] = ["Direct", "Semi Direct", "Indirect"];

interface Draft {
  payrollName: string;
  iplicitDept: string;
  class: DeptClass;
  costCentre: string;
}

export default function ImportReview({
  preview, busy, onApply, onCancel,
}: {
  preview: ImportPreview;
  busy: boolean;
  onApply: (edits: DeptEdit[], overwrite: boolean) => void;
  onCancel: () => void;
}) {
  const { reconciliation: rec, summary } = preview;
  const [overwrite, setOverwrite] = useState(true);

  const [drafts, setDrafts] = useState<Draft[]>(() =>
    rec.needsReview.map((r) => ({
      payrollName: r.payrollName,
      iplicitDept: r.current?.iplicitDept ?? "",
      class: r.current?.class ?? "Direct",
      costCentre: r.current?.costCentre ?? (rec.costCentres.find((c) => c.code === "C9999")?.code ?? rec.costCentres[0]?.code ?? "C9999"),
    }))
  );

  const set = (i: number, patch: Partial<Draft>) =>
    setDrafts((ds) => ds.map((d, k) => (k === i ? { ...d, ...patch } : d)));

  const allChosen = useMemo(() => drafts.every((d) => d.iplicitDept.trim().length > 0), [drafts]);

  return (
    <div className="panel import-review">
      <header className="panel__head">
        <div>
          <h3>Check departments before posting</h3>
          <p className="panel__sub">
            {summary.monthLabel} · {summary.activeDepartments} departments with staff ·{" "}
            {rec.needsReview.length} need{rec.needsReview.length === 1 ? "s" : ""} a decision.
          </p>
        </div>
      </header>

      <div className="table-wrap">
        <table className="table import-review__table">
          <thead>
            <tr>
              <th>Payroll department</th>
              <th></th>
              <th>iplicit department</th>
              <th>Class</th>
              <th>Cost centre</th>
            </tr>
          </thead>
          <tbody>
            {rec.needsReview.map((r, i) => (
              <tr key={r.payrollName}>
                <td>
                  <div>{r.payrollName}</div>
                  <div className="muted import-review__reason">{r.reason}</div>
                </td>
                <td>
                  <span className={`badge ${r.kind === "new" ? "badge--warn" : "badge--bad"}`}>
                    {r.kind === "new" ? "New" : "Check mapping"}
                  </span>
                </td>
                <td>
                  <select
                    className="select"
                    value={drafts[i].iplicitDept}
                    onChange={(e) => set(i, { iplicitDept: e.target.value })}
                  >
                    <option value="">— choose —</option>
                    {rec.iplicitDepartments.map((d) => (
                      <option key={d.code} value={d.code}>{d.code} · {d.description}</option>
                    ))}
                    {/* Keep a stored-but-unknown code visible rather than silently blank. */}
                    {drafts[i].iplicitDept &&
                      !rec.iplicitDepartments.some((d) => d.code === drafts[i].iplicitDept) && (
                        <option value={drafts[i].iplicitDept}>{drafts[i].iplicitDept} (not in iplicit)</option>
                      )}
                  </select>
                </td>
                <td>
                  <select className="select" value={drafts[i].class} onChange={(e) => set(i, { class: e.target.value as DeptClass })}>
                    {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td>
                  <select className="select" value={drafts[i].costCentre} onChange={(e) => set(i, { costCentre: e.target.value })}>
                    {rec.costCentres.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rec.ignored.length > 0 && (
        <p className="muted import-review__ignored">
          Ignored this month (no staff, left out of the journal): {rec.ignored.join(", ")}.
        </p>
      )}

      <div className="import-review__foot">
        <label className="pill-toggle" title="When on, the values above replace existing department rows. When off, only brand-new departments are added and existing rows are left unchanged.">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
          <span>Overwrite existing departments with these values</span>
        </label>

        <div className="import-review__buttons">
          <button className="btn" disabled={busy} onClick={onCancel}>Cancel import</button>
          <button
            className="btn btn--primary"
            disabled={busy || !allChosen}
            title={allChosen ? "" : "Give every department an iplicit department first."}
            onClick={() => onApply(drafts.map((d) => ({ ...d, iplicitDept: d.iplicitDept.trim() })), overwrite)}
          >
            {busy ? "Importing…" : "Apply & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
